import React from 'react';
import styled from 'styled-components';
import { Button, GhostButton, Link } from '../buttons';
import { BreadcumbContainer } from '../buttons/breadcumb';
import { Icon } from '../icon';
import { Steps } from '../steps';

const PageHeaderWrap = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    position: sticky;
    top: 0px;
    z-index: 2;
    gap: 8px;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    padding-top: 16px;
    padding-bottom: 16px;
`;

const PageHeaderBackSteps = styled.div`
    display: flex;
    flex: 1 0 100%;
    padding: 0;

    ${Button} {
        margin-left: 10px;
    }
    ${BreadcumbContainer} {
        padding: 0;
        text-transform: none;
        ${Icon} {
            margin-right: 2px;
            margin-left: -7px;
        }
    }
`;
const PageHeaderSteps = styled.div`
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
    padding-top: 3px;
    min-height: 26px;
`;
const PageHeaderTitleDescription = styled.div`
    flex: 1 0;
    color: ${(props) => props.theme.app.textColor};
    min-height: ${(props) => props.theme.app.component.button.medium.height};
    display: flex;
    flex-direction: column;
    justify-content: center;
`;
const PageHeaderTitle = styled.h2`
    margin: 0 !important;
    display: flex;
    align-items: center;
    font-size: ${(props) => props.theme.app.component.pageHeader.title.size};
    font-weight: ${(props) => props.theme.app.component.pageHeader.title.weight};
    font-family: ${(props) => props.theme.app.font};
    line-height: ${(props) => props.theme.app.component.pageHeader.lineHeight};
    color: #263238;
`;

const PageHeaderDescription = styled.div<{ $hasDescription: boolean }>`
    padding-block-start: 8px;
    display: flex;
    line-height: ${(props) => props.theme.app.component.pageHeader.lineHeight};
    color: #455a64;
    font-size: 13px;

    > a {
        margin-left: ${(props) => (props.$hasDescription ? '4px' : '0')} !important;
    }

    /* Reset margin for paragraphs inside the description when data is from database */
    > p {
        margin: 0;
    }
`;

const PageHeaderLearnMore = styled(Link)`
    display: inline-block;
`;

const PageHeaderButtonsContainer = styled.div`
    position: sticky;
    top: 0px;
    z-index: 17;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    margin: auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;

    ${Button} {
        margin-left: 10px;
    }

    ${BreadcumbContainer} {
        margin-right: auto;
    }
`;

export const PageHeaderButtons = ({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <PageHeaderButtonsContainer className={className} {...props}>
            {children}
        </PageHeaderButtonsContainer>
    );
};

export const BackAndSteps = styled(PageHeaderButtons)`
    max-width: ${(props) => props.theme.size.container};
    padding: 0 20px 0px 20px;

    ${Steps} {
        width: 100%;
    }
`;

export const FullPageHeaderButtons = styled(PageHeaderButtons)`
    max-width: 100%;
`;

export const PageHeaderButtonsWrap = styled.div`
    display: flex;
    align-items: center;
    align-self: flex-start;
    gap: 8px;
    margin-inline-start: auto;
    flex-shrink: 0;

    ${GhostButton} {
        background-color: ${(props) => props.theme.app.component.button.basic.default.backgroundColor};
        border: ${(props) => props.theme.app.component.button.basic.border};
        color: ${(props) => props.theme.app.component.button.basic.default.textColor};

        &:hover:not([disabled]) {
            background-color: ${(props) => props.theme.app.component.button.basic.hover.backgroundColor};
            color: ${(props) => props.theme.app.component.button.basic.hover.textColor};
        }

        &:active:not([disabled]) {
            background-color: ${(props) => props.theme.app.component.button.basic.active.backgroundColor};
            color: ${(props) => props.theme.app.component.button.basic.active.textColor};
        }
    }
`;

const PageHeaderCompactWrap = styled.div`
    position: sticky;
    top: 0px;
    z-index: 2;
    padding: 10px 0 24px;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
`;

const PageHeaderCompactRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: ${(props) => props.theme.app.component.button.medium.height};

    ${BreadcumbContainer} {
        padding: 0;
        text-transform: none;
        ${Icon} {
            margin-right: 2px;
            margin-left: -7px;
        }
    }

    ${PageHeaderTitle} {
        flex: 1;
    }
`;

const PageHeaderCompactContainer = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    return <PageHeaderCompactWrap className={className}>{children}</PageHeaderCompactWrap>;
};

PageHeaderContainer.BackSteps = PageHeaderBackSteps;
PageHeaderContainer.Steps = PageHeaderSteps;
PageHeaderContainer.TitleDescription = PageHeaderTitleDescription;
PageHeaderContainer.Title = PageHeaderTitle;
PageHeaderContainer.Description = PageHeaderDescription;
PageHeaderContainer.LearnMore = PageHeaderLearnMore;
PageHeaderContainer.Buttons = PageHeaderButtonsWrap;
PageHeaderContainer.CompactWrap = PageHeaderCompactContainer;
PageHeaderContainer.CompactRow = PageHeaderCompactRow;

export function PageHeaderContainer({ children, className }: { children?: React.ReactNode; className?: string }) {
    return <PageHeaderWrap className={className}>{children}</PageHeaderWrap>;
}
